import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { useImpersonation } from '@/contexts/ImpersonationContext'
import type { Tables } from '@limo/database'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { CurrencyInput } from '@/components/ui/currency-input'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { useToast } from '@/hooks/use-toast'
import { Loader2, Plus, Pencil, Trash2 } from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'
import { PriceAdjustmentDialog } from '@/components/pricing/PriceAdjustmentDialog'
import { MinimumHourAdjustmentDialog } from '@/components/pricing/MinimumHourAdjustmentDialog'
import { CustomFeeDialog } from '@/components/pricing/CustomFeeDialog'
import { formatPrice } from '@limo/shared'

type PriceAdjustment = Tables<'price_adjustments'>
type MinimumHourAdjustment = Tables<'minimum_hour_adjustments'>
type CustomFee = Tables<'custom_fees'>

// Setting keys
const PRICING_SETTINGS = {
  GRATUITY_PERCENT: 'pricing_gratuity_percent',
  TAX_ENABLED: 'pricing_tax_enabled',
  TAX_RATE: 'pricing_tax_rate',
  TAX_LABEL: 'pricing_tax_label',
  FUEL_SURCHARGE_ENABLED: 'pricing_fuel_surcharge_enabled',
  FUEL_SURCHARGE_TYPE: 'pricing_fuel_surcharge_type',
  FUEL_SURCHARGE_AMOUNT: 'pricing_fuel_surcharge_amount',
  FUEL_SURCHARGE_LABEL: 'pricing_fuel_surcharge_label',
  AIRPORT_FEE_ENABLED: 'pricing_airport_fee_enabled',
  AIRPORT_PICKUP_FEE: 'pricing_airport_pickup_fee',
  AIRPORT_DROPOFF_FEE: 'pricing_airport_dropoff_fee',
  MEET_GREET_ENABLED: 'pricing_meet_greet_enabled',
  MEET_GREET_FEE: 'pricing_meet_greet_fee',
  MINIMUM_FARE: 'pricing_minimum_fare',
  ROUND_TO: 'pricing_round_to',
  ROUND_DIRECTION: 'pricing_round_direction',
  VEHICLE_DISPLAY_ORDER: 'pricing_vehicle_display_order',
  SHOW_INCLUSIVE_BREAKDOWN: 'pricing_show_inclusive_breakdown',
} as const

// Schema
const pricingSchema = z.object({
  gratuity_percent: z.coerce.number().min(0).max(50, 'Maximum 50%').optional(),
  show_inclusive_breakdown: z.boolean().default(false),
  tax_enabled: z.boolean().default(false),
  tax_rate: z.coerce.number().min(0).max(50, 'Maximum 50%').optional(),
  tax_label: z.string().max(50).optional(),
  fuel_surcharge_enabled: z.boolean().default(false),
  fuel_surcharge_type: z.enum(['percentage', 'flat']).default('percentage'),
  fuel_surcharge_amount: z.coerce.number().min(0).optional(),
  fuel_surcharge_label: z.string().max(50).optional(),
  airport_fee_enabled: z.boolean().default(false),
  airport_pickup_fee: z.coerce.number().min(0).optional(),
  airport_dropoff_fee: z.coerce.number().min(0).optional(),
  meet_greet_enabled: z.boolean().default(false),
  meet_greet_fee: z.coerce.number().min(0).optional(),
  minimum_fare: z.coerce.number().min(0).optional(),
  round_to: z.enum(['cent', 'dollar', 'five_dollars']).default('cent'),
  round_direction: z.enum(['up', 'down', 'nearest']).default('nearest'),
  vehicle_display_order: z
    .enum(['passenger_capacity', 'price_asc', 'price_desc', 'random'])
    .default('passenger_capacity'),
})

type PricingFormData = z.infer<typeof pricingSchema>

const DAYS_ABBREV = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

function formatDaysOfWeek(days: number[]): string {
  if (days.length === 7) return 'Every day'
  if (
    days.length === 2 &&
    days.includes(0) &&
    days.includes(6)
  ) {
    return 'Weekends'
  }
  if (
    days.length === 5 &&
    !days.includes(0) &&
    !days.includes(6)
  ) {
    return 'Weekdays'
  }
  return days.map((d) => DAYS_ABBREV[d]).join(', ')
}

function formatTime(time: string): string {
  // Convert "HH:MM:SS" to "HH:MM AM/PM"
  const [hours, minutes] = time.split(':')
  const h = parseInt(hours, 10)
  const ampm = h >= 12 ? 'PM' : 'AM'
  const h12 = h % 12 || 12
  return `${h12}:${minutes} ${ampm}`
}

export function PricingPage() {
  const { account } = useAuth()
  const { isImpersonating, impersonatedAccount } = useImpersonation()
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const activeAccount = isImpersonating ? impersonatedAccount : account

  // State for price adjustment dialogs
  const [adjustmentDialogOpen, setAdjustmentDialogOpen] = useState(false)
  const [editingAdjustment, setEditingAdjustment] = useState<PriceAdjustment | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deletingAdjustment, setDeletingAdjustment] = useState<PriceAdjustment | null>(null)

  // State for custom fee dialogs
  const [customFeeDialogOpen, setCustomFeeDialogOpen] = useState(false)
  const [editingCustomFee, setEditingCustomFee] = useState<CustomFee | null>(null)
  const [deleteCustomFeeDialogOpen, setDeleteCustomFeeDialogOpen] = useState(false)
  const [deletingCustomFee, setDeletingCustomFee] = useState<CustomFee | null>(null)

  // State for minimum hour adjustment dialogs
  const [minHourDialogOpen, setMinHourDialogOpen] = useState(false)
  const [editingMinHourRule, setEditingMinHourRule] = useState<MinimumHourAdjustment | null>(null)
  const [deleteMinHourDialogOpen, setDeleteMinHourDialogOpen] = useState(false)
  const [deletingMinHourRule, setDeletingMinHourRule] = useState<MinimumHourAdjustment | null>(null)

  // Fetch account settings
  const { data: settings, isLoading: settingsLoading } = useQuery({
    queryKey: ['account-settings', activeAccount?.id],
    queryFn: async () => {
      if (!activeAccount?.id) return {}

      const { data, error } = await supabase
        .from('account_settings')
        .select('*')
        .eq('account_id', activeAccount.id)

      if (error) throw error

      const settingsMap: Record<string, string> = {}
      data?.forEach((setting) => {
        settingsMap[setting.key] = setting.value || ''
      })
      return settingsMap
    },
    enabled: !!activeAccount?.id,
  })

  // Fetch active vehicles for vehicle selection in price adjustments
  const { data: vehicles } = useQuery({
    queryKey: ['vehicles-active', activeAccount?.id],
    queryFn: async () => {
      if (!activeAccount?.id) return []

      const { data, error } = await supabase
        .from('vehicles')
        .select('id, name')
        .eq('account_id', activeAccount.id)
        .eq('is_active', true)
        .order('display_order', { ascending: true })

      if (error) throw error
      return data || []
    },
    enabled: !!activeAccount?.id,
  })

  // Fetch price adjustments
  const { data: adjustments, isLoading: adjustmentsLoading } = useQuery({
    queryKey: ['price-adjustments', activeAccount?.id],
    queryFn: async () => {
      if (!activeAccount?.id) return []

      const { data, error } = await supabase
        .from('price_adjustments')
        .select('*')
        .eq('account_id', activeAccount.id)
        .order('priority', { ascending: true })

      if (error) throw error
      return data || []
    },
    enabled: !!activeAccount?.id,
  })

  // Fetch custom fees
  const { data: customFees, isLoading: customFeesLoading } = useQuery({
    queryKey: ['custom-fees', activeAccount?.id],
    queryFn: async () => {
      if (!activeAccount?.id) return []

      const { data, error } = await supabase
        .from('custom_fees')
        .select('*')
        .eq('account_id', activeAccount.id)
        .order('sort_order', { ascending: true })

      if (error) throw error
      return data || []
    },
    enabled: !!activeAccount?.id,
  })

  // Fetch minimum hour adjustments
  const { data: minHourRules, isLoading: minHourRulesLoading } = useQuery({
    queryKey: ['minimum-hour-adjustments', activeAccount?.id],
    queryFn: async () => {
      if (!activeAccount?.id) return []

      const { data, error } = await supabase
        .from('minimum_hour_adjustments')
        .select('*')
        .eq('account_id', activeAccount.id)
        .order('priority', { ascending: true })

      if (error) throw error
      return data || []
    },
    enabled: !!activeAccount?.id,
  })

  // Form
  const form = useForm<PricingFormData>({
    resolver: zodResolver(pricingSchema),
    defaultValues: {
      gratuity_percent: 0,
      show_inclusive_breakdown: false,
      tax_enabled: false,
      tax_rate: 0,
      tax_label: 'Sales Tax',
      fuel_surcharge_enabled: false,
      fuel_surcharge_type: 'percentage',
      fuel_surcharge_amount: 0,
      fuel_surcharge_label: 'Fuel Surcharge',
      airport_fee_enabled: false,
      airport_pickup_fee: 0,
      airport_dropoff_fee: 0,
      meet_greet_enabled: false,
      meet_greet_fee: 0,
      minimum_fare: 0,
      round_to: 'cent',
      round_direction: 'nearest',
      vehicle_display_order: 'passenger_capacity',
    },
  })

  // Watch toggles for conditional rendering
  const taxEnabled = form.watch('tax_enabled')
  const fuelSurchargeEnabled = form.watch('fuel_surcharge_enabled')
  const airportFeeEnabled = form.watch('airport_fee_enabled')
  const meetGreetEnabled = form.watch('meet_greet_enabled')

  // Update form when settings are loaded
  useEffect(() => {
    if (settings) {
      form.reset({
        gratuity_percent: settings[PRICING_SETTINGS.GRATUITY_PERCENT]
          ? Number(settings[PRICING_SETTINGS.GRATUITY_PERCENT])
          : 0,
        show_inclusive_breakdown:
          settings[PRICING_SETTINGS.SHOW_INCLUSIVE_BREAKDOWN] === 'true',
        tax_enabled: settings[PRICING_SETTINGS.TAX_ENABLED] === 'true',
        tax_rate: settings[PRICING_SETTINGS.TAX_RATE]
          ? Number(settings[PRICING_SETTINGS.TAX_RATE])
          : 0,
        tax_label: settings[PRICING_SETTINGS.TAX_LABEL] || 'Sales Tax',
        fuel_surcharge_enabled: settings[PRICING_SETTINGS.FUEL_SURCHARGE_ENABLED] === 'true',
        fuel_surcharge_type:
          (settings[PRICING_SETTINGS.FUEL_SURCHARGE_TYPE] as 'percentage' | 'flat') || 'percentage',
        fuel_surcharge_amount: settings[PRICING_SETTINGS.FUEL_SURCHARGE_AMOUNT]
          ? Number(settings[PRICING_SETTINGS.FUEL_SURCHARGE_AMOUNT])
          : 0,
        fuel_surcharge_label: settings[PRICING_SETTINGS.FUEL_SURCHARGE_LABEL] || 'Fuel Surcharge',
        airport_fee_enabled: settings[PRICING_SETTINGS.AIRPORT_FEE_ENABLED] === 'true',
        airport_pickup_fee: settings[PRICING_SETTINGS.AIRPORT_PICKUP_FEE]
          ? Number(settings[PRICING_SETTINGS.AIRPORT_PICKUP_FEE])
          : 0,
        airport_dropoff_fee: settings[PRICING_SETTINGS.AIRPORT_DROPOFF_FEE]
          ? Number(settings[PRICING_SETTINGS.AIRPORT_DROPOFF_FEE])
          : 0,
        meet_greet_enabled: settings[PRICING_SETTINGS.MEET_GREET_ENABLED] === 'true',
        meet_greet_fee: settings[PRICING_SETTINGS.MEET_GREET_FEE]
          ? Number(settings[PRICING_SETTINGS.MEET_GREET_FEE])
          : 0,
        minimum_fare: settings[PRICING_SETTINGS.MINIMUM_FARE]
          ? Number(settings[PRICING_SETTINGS.MINIMUM_FARE])
          : 0,
        round_to:
          (settings[PRICING_SETTINGS.ROUND_TO] as 'cent' | 'dollar' | 'five_dollars') || 'cent',
        round_direction:
          (settings[PRICING_SETTINGS.ROUND_DIRECTION] as 'up' | 'down' | 'nearest') || 'nearest',
        vehicle_display_order:
          (settings[PRICING_SETTINGS.VEHICLE_DISPLAY_ORDER] as
            | 'passenger_capacity'
            | 'price_asc'
            | 'price_desc'
            | 'random') || 'passenger_capacity',
      })
    }
  }, [settings, form])

  // Save settings mutation
  const saveMutation = useMutation({
    mutationFn: async (data: PricingFormData) => {
      if (!activeAccount?.id) throw new Error('No account found')

      const settingsToUpsert = [
        {
          account_id: activeAccount.id,
          key: PRICING_SETTINGS.GRATUITY_PERCENT,
          value: String(data.gratuity_percent ?? 0),
          description: 'Default gratuity percentage',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: PRICING_SETTINGS.SHOW_INCLUSIVE_BREAKDOWN,
          value: String(data.show_inclusive_breakdown),
          description: 'Show tax & gratuity breakdown under the price on vehicle cards',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: PRICING_SETTINGS.TAX_ENABLED,
          value: String(data.tax_enabled),
          description: 'Enable tax display',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: PRICING_SETTINGS.TAX_RATE,
          value: String(data.tax_rate ?? 0),
          description: 'Tax rate percentage',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: PRICING_SETTINGS.TAX_LABEL,
          value: data.tax_label || 'Sales Tax',
          description: 'Custom tax label',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: PRICING_SETTINGS.FUEL_SURCHARGE_ENABLED,
          value: String(data.fuel_surcharge_enabled),
          description: 'Enable fuel surcharge',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: PRICING_SETTINGS.FUEL_SURCHARGE_TYPE,
          value: data.fuel_surcharge_type || 'percentage',
          description: 'Fuel surcharge type',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: PRICING_SETTINGS.FUEL_SURCHARGE_AMOUNT,
          value: String(data.fuel_surcharge_amount ?? 0),
          description: 'Fuel surcharge amount',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: PRICING_SETTINGS.FUEL_SURCHARGE_LABEL,
          value: data.fuel_surcharge_label || 'Fuel Surcharge',
          description: 'Custom fuel surcharge label',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: PRICING_SETTINGS.AIRPORT_FEE_ENABLED,
          value: String(data.airport_fee_enabled),
          description: 'Enable airport fees',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: PRICING_SETTINGS.AIRPORT_PICKUP_FEE,
          value: String(data.airport_pickup_fee ?? 0),
          description: 'Airport pickup fee',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: PRICING_SETTINGS.AIRPORT_DROPOFF_FEE,
          value: String(data.airport_dropoff_fee ?? 0),
          description: 'Airport drop-off fee',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: PRICING_SETTINGS.MEET_GREET_ENABLED,
          value: String(data.meet_greet_enabled),
          description: 'Enable meet & greet service',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: PRICING_SETTINGS.MEET_GREET_FEE,
          value: String(data.meet_greet_fee ?? 0),
          description: 'Meet & greet fee',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: PRICING_SETTINGS.MINIMUM_FARE,
          value: String(data.minimum_fare ?? 0),
          description: 'Minimum fare for bookings',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: PRICING_SETTINGS.ROUND_TO,
          value: data.round_to || 'cent',
          description: 'Price rounding precision',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: PRICING_SETTINGS.ROUND_DIRECTION,
          value: data.round_direction || 'nearest',
          description: 'Price rounding direction',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: PRICING_SETTINGS.VEHICLE_DISPLAY_ORDER,
          value: data.vehicle_display_order || 'passenger_capacity',
          description: 'Order vehicles are displayed in the booking flow',
          is_secret: false,
        },
      ]

      for (const setting of settingsToUpsert) {
        const { error } = await supabase
          .from('account_settings')
          .upsert(setting, { onConflict: 'account_id,key' })

        if (error) throw error
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-settings', activeAccount?.id] })
      toast({
        title: 'Pricing settings saved',
        description: 'Your pricing settings have been updated.',
      })
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  // Delete adjustment mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('price_adjustments').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['price-adjustments', activeAccount?.id] })
      toast({
        title: 'Adjustment deleted',
        description: 'The price adjustment has been removed.',
      })
      setDeleteDialogOpen(false)
      setDeletingAdjustment(null)
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  // Delete minimum hour rule mutation
  const deleteMinHourMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('minimum_hour_adjustments').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['minimum-hour-adjustments', activeAccount?.id] })
      toast({
        title: 'Rule deleted',
        description: 'The minimum hour rule has been removed.',
      })
      setDeleteMinHourDialogOpen(false)
      setDeletingMinHourRule(null)
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  const handleAddAdjustment = () => {
    setEditingAdjustment(null)
    setAdjustmentDialogOpen(true)
  }

  const handleEditAdjustment = (adjustment: PriceAdjustment) => {
    setEditingAdjustment(adjustment)
    setAdjustmentDialogOpen(true)
  }

  const handleDeleteAdjustment = (adjustment: PriceAdjustment) => {
    setDeletingAdjustment(adjustment)
    setDeleteDialogOpen(true)
  }

  const handleAddMinHourRule = () => {
    setEditingMinHourRule(null)
    setMinHourDialogOpen(true)
  }

  const handleEditMinHourRule = (rule: MinimumHourAdjustment) => {
    setEditingMinHourRule(rule)
    setMinHourDialogOpen(true)
  }

  const handleDeleteMinHourRule = (rule: MinimumHourAdjustment) => {
    setDeletingMinHourRule(rule)
    setDeleteMinHourDialogOpen(true)
  }

  // Custom fee handlers
  const deleteCustomFeeMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('custom_fees').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['custom-fees', activeAccount?.id] })
      toast({
        title: 'Fee deleted',
        description: 'The custom fee has been removed.',
      })
      setDeleteCustomFeeDialogOpen(false)
      setDeletingCustomFee(null)
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  const handleAddCustomFee = () => {
    setEditingCustomFee(null)
    setCustomFeeDialogOpen(true)
  }

  const handleEditCustomFee = (fee: CustomFee) => {
    setEditingCustomFee(fee)
    setCustomFeeDialogOpen(true)
  }

  const handleDeleteCustomFee = (fee: CustomFee) => {
    setDeletingCustomFee(fee)
    setDeleteCustomFeeDialogOpen(true)
  }

  const isLoading = settingsLoading || adjustmentsLoading || minHourRulesLoading || customFeesLoading

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-4 w-64" />
          </CardHeader>
          <CardContent className="space-y-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-24" />
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit((data) => saveMutation.mutate(data))}
          className="space-y-6"
        >
          {/* Gratuity Section */}
          <Card>
            <CardHeader>
              <CardTitle>Gratuity & Tips</CardTitle>
              <CardDescription>
                Set a default gratuity percentage for all bookings
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <FormField
                control={form.control}
                name="gratuity_percent"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Auto-Gratuity (%)</FormLabel>
                    <FormControl>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min={0}
                          max={50}
                          step={1}
                          className="w-24"
                          {...field}
                        />
                        <span className="text-muted-foreground">%</span>
                      </div>
                    </FormControl>
                    <FormDescription>
                      Set to 0 for no automatic gratuity
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="show_inclusive_breakdown"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5 pr-4">
                      <FormLabel className="text-base">
                        Show tax &amp; gratuity breakdown on vehicle cards
                      </FormLabel>
                      <FormDescription>
                        When on, the note under each vehicle price reads e.g.
                        &ldquo;No surprises — 10.3% tax, 20% gratuity &amp; all fees
                        included&rdquo; using your configured rates. When off, it
                        shows the default &ldquo;Price includes taxes, tolls. No
                        hidden costs.&rdquo;
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Taxes & Fees Section */}
          <Card>
            <CardHeader>
              <CardTitle>Taxes & Fees</CardTitle>
              <CardDescription>
                Configure tax rates, fuel surcharges, and airport fees
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Tax Settings */}
              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="tax_enabled"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <FormLabel className="text-base">Tax</FormLabel>
                        <FormDescription>
                          Add sales tax to booking totals
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                {taxEnabled && (
                  <div className="ml-4 grid gap-4 sm:grid-cols-2">
                    <FormField
                      control={form.control}
                      name="tax_rate"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Tax Rate (%)</FormLabel>
                          <FormControl>
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                min={0}
                                max={50}
                                step={0.1}
                                className="w-24"
                                {...field}
                              />
                              <span className="text-muted-foreground">%</span>
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="tax_label"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Tax Label</FormLabel>
                          <FormControl>
                            <Input placeholder="Sales Tax" {...field} />
                          </FormControl>
                          <FormDescription className="text-xs">
                            e.g., Sales Tax, GST, VAT
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                )}
              </div>

              {/* Fuel Surcharge Settings */}
              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="fuel_surcharge_enabled"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <FormLabel className="text-base">Fuel Surcharge</FormLabel>
                        <FormDescription>
                          Add a fuel surcharge to bookings
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                {fuelSurchargeEnabled && (
                  <div className="ml-4 space-y-4">
                    <div className="grid gap-4 sm:grid-cols-2">
                      <FormField
                        control={form.control}
                        name="fuel_surcharge_type"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Surcharge Type</FormLabel>
                            <Select
                              onValueChange={field.onChange}
                              value={field.value}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="percentage">Percentage</SelectItem>
                                <SelectItem value="flat">Flat Rate</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="fuel_surcharge_amount"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Amount</FormLabel>
                            <FormControl>
                              {form.watch('fuel_surcharge_type') === 'percentage' ? (
                                <div className="flex items-center gap-2">
                                  <Input
                                    type="number"
                                    min={0}
                                    step={0.01}
                                    className="w-24"
                                    {...field}
                                  />
                                  <span className="text-muted-foreground">%</span>
                                </div>
                              ) : (
                                <CurrencyInput
                                  className="w-32"
                                  value={field.value}
                                  onChange={(value) => field.onChange(value ?? 0)}
                                />
                              )}
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <FormField
                      control={form.control}
                      name="fuel_surcharge_label"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Surcharge Label</FormLabel>
                          <FormControl>
                            <Input placeholder="Fuel Surcharge" className="max-w-xs" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                )}
              </div>

              {/* Airport Fees */}
              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="airport_fee_enabled"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <FormLabel className="text-base">Airport Fees</FormLabel>
                        <FormDescription>
                          Add fees for airport pickups and drop-offs
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                {airportFeeEnabled && (
                  <div className="ml-4 grid gap-4 sm:grid-cols-2">
                    <FormField
                      control={form.control}
                      name="airport_pickup_fee"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Airport Pickup Fee</FormLabel>
                          <FormControl>
                            <CurrencyInput
                              value={field.value}
                              onChange={(value) => field.onChange(value ?? 0)}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="airport_dropoff_fee"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Airport Drop-off Fee</FormLabel>
                          <FormControl>
                            <CurrencyInput
                              value={field.value}
                              onChange={(value) => field.onChange(value ?? 0)}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Custom Fees Section */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Custom Fees</CardTitle>
                  <CardDescription>
                    Add custom fees that are applied to every booking (e.g., credit card fee, handling fee)
                  </CardDescription>
                </div>
                <Button size="sm" onClick={handleAddCustomFee}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Fee
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {customFees && customFees.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Taxable</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="w-[100px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {customFees.map((fee) => (
                      <TableRow key={fee.id}>
                        <TableCell className="font-medium">{fee.name}</TableCell>
                        <TableCell>
                          {fee.fee_type === 'flat'
                            ? formatPrice(Number(fee.amount))
                            : `${fee.amount}%`}
                        </TableCell>
                        <TableCell>
                          <Badge variant={fee.is_taxable ? 'default' : 'secondary'}>
                            {fee.is_taxable ? 'Taxable' : 'Non-taxable'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={fee.is_active ? 'default' : 'outline'}>
                            {fee.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEditCustomFee(fee)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteCustomFee(fee)}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No custom fees configured. Add a fee to get started.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Service Add-ons Section */}
          <Card>
            <CardHeader>
              <CardTitle>Service Add-ons</CardTitle>
              <CardDescription>
                Optional services customers can add to their booking
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="meet_greet_enabled"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <FormLabel className="text-base">Meet & Greet Service</FormLabel>
                        <FormDescription>
                          Offer meet & greet for airport pickups
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                {meetGreetEnabled && (
                  <FormField
                    control={form.control}
                    name="meet_greet_fee"
                    render={({ field }) => (
                      <FormItem className="ml-4">
                        <FormLabel>Meet & Greet Fee</FormLabel>
                        <FormControl>
                          <CurrencyInput
                            className="w-32"
                            value={field.value}
                            onChange={(value) => field.onChange(value ?? 0)}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </div>
            </CardContent>
          </Card>

          {/* Price Adjustments Section */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Price Adjustments</CardTitle>
                <CardDescription>
                  Time-based surge pricing or discounts for peak hours, slow
                  periods, or special dates
                </CardDescription>
              </div>
              <Button type="button" size="sm" onClick={handleAddAdjustment}>
                <Plus className="mr-2 h-4 w-4" />
                Add Adjustment
              </Button>
            </CardHeader>
            <CardContent>
              {adjustments && adjustments.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Time</TableHead>
                      <TableHead>Days</TableHead>
                      <TableHead>Vehicles</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="w-[100px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {adjustments.map((adj) => (
                      <TableRow key={adj.id}>
                        <TableCell className="font-medium">{adj.name}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatTime(adj.start_time)} - {formatTime(adj.end_time)}
                        </TableCell>
                        <TableCell className="text-sm">
                          {formatDaysOfWeek(adj.days_of_week)}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {adj.vehicle_ids
                            ? `${adj.vehicle_ids.length} vehicle(s)`
                            : 'All vehicles'}
                        </TableCell>
                        <TableCell>
                          {(() => {
                            const isDiscount = adj.adjustment_type === 'decrease'
                            const isFlat = adj.amount_type === 'flat'
                            const sign = isDiscount ? '-' : '+'
                            const value = isFlat
                              ? `$${Number(adj.flat_amount ?? 0).toFixed(2)}`
                              : `${adj.percentage_increase}%`
                            return (
                              <span className={isDiscount ? 'text-green-700 font-medium' : undefined}>
                                {sign}
                                {value}
                              </span>
                            )
                          })()}
                        </TableCell>
                        <TableCell>
                          <Badge variant={adj.is_active ? 'default' : 'secondary'}>
                            {adj.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEditAdjustment(adj)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteAdjustment(adj)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-6 text-muted-foreground">
                  No price adjustments configured. Add one to enable surge pricing or discounts.
                </div>
              )}
            </CardContent>
          </Card>

          {/* Minimum Hour Adjustments Section */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Minimum Hour Adjustments</CardTitle>
                <CardDescription>
                  Day and time-based minimum hour requirements per vehicle. Airport bookings are always exempt.
                </CardDescription>
              </div>
              <Button type="button" size="sm" onClick={handleAddMinHourRule}>
                <Plus className="mr-2 h-4 w-4" />
                Add Rule
              </Button>
            </CardHeader>
            <CardContent>
              {minHourRules && minHourRules.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Time</TableHead>
                      <TableHead>Days</TableHead>
                      <TableHead>Vehicles</TableHead>
                      <TableHead>Min Hours</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="w-[100px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {minHourRules.map((rule) => (
                      <TableRow key={rule.id}>
                        <TableCell className="font-medium">{rule.name}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {rule.start_time && rule.end_time
                            ? `${formatTime(rule.start_time)} - ${formatTime(rule.end_time)}`
                            : 'All Day'}
                        </TableCell>
                        <TableCell className="text-sm">
                          {formatDaysOfWeek(rule.days_of_week)}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {rule.vehicle_ids
                            ? `${rule.vehicle_ids.length} vehicle(s)`
                            : 'All vehicles'}
                        </TableCell>
                        <TableCell>{rule.minimum_hours}h</TableCell>
                        <TableCell>
                          <Badge variant={rule.is_active ? 'default' : 'secondary'}>
                            {rule.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEditMinHourRule(rule)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteMinHourRule(rule)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-6 text-muted-foreground">
                  No minimum hour rules configured. Add one to enforce minimum booking hours during specific times.
                </div>
              )}
            </CardContent>
          </Card>

          {/* Minimums & Rounding Section */}
          <Card>
            <CardHeader>
              <CardTitle>Minimums & Rounding</CardTitle>
              <CardDescription>
                Set minimum fares and price rounding rules
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <FormField
                control={form.control}
                name="minimum_fare"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Minimum Fare</FormLabel>
                    <FormControl>
                      <CurrencyInput
                        className="w-32"
                        value={field.value}
                        onChange={(value) => field.onChange(value ?? 0)}
                      />
                    </FormControl>
                    <FormDescription>
                      Minimum charge for any booking. Set to 0 for no minimum.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid gap-4 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="round_to"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Round To</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="cent">Cent ($0.01)</SelectItem>
                          <SelectItem value="dollar">Dollar ($1.00)</SelectItem>
                          <SelectItem value="five_dollars">Five Dollars ($5.00)</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="round_direction"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Rounding Direction</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="nearest">Nearest</SelectItem>
                          <SelectItem value="up">Always Up</SelectItem>
                          <SelectItem value="down">Always Down</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>
          </Card>

          {/* Vehicle Display Section */}
          <Card>
            <CardHeader>
              <CardTitle>Vehicle Display</CardTitle>
              <CardDescription>
                Control how vehicles are ordered for customers on the booking page
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <FormField
                control={form.control}
                name="vehicle_display_order"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Vehicle Display Order</FormLabel>
                    <Select
                      key={field.value}
                      onValueChange={field.onChange}
                      value={field.value}
                    >
                      <FormControl>
                        <SelectTrigger className="sm:w-80">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="passenger_capacity">
                          Default (by passenger capacity)
                        </SelectItem>
                        <SelectItem value="price_asc">
                          Cheapest to most expensive
                        </SelectItem>
                        <SelectItem value="price_desc">
                          Most expensive to cheapest
                        </SelectItem>
                        <SelectItem value="random">Random</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      Sets the order vehicles appear in on the booking page after prices are calculated. &quot;Random&quot; reshuffles on every page load.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <Button type="submit" disabled={saveMutation.isPending}>
            {saveMutation.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Save Changes
          </Button>
        </form>
      </Form>

      {/* Price Adjustment Dialog */}
      {activeAccount && (
        <PriceAdjustmentDialog
          open={adjustmentDialogOpen}
          onOpenChange={setAdjustmentDialogOpen}
          adjustment={editingAdjustment}
          accountId={activeAccount.id}
          vehicles={vehicles || []}
          existingAdjustments={adjustments || []}
        />
      )}

      {/* Minimum Hour Adjustment Dialog */}
      {activeAccount && (
        <MinimumHourAdjustmentDialog
          open={minHourDialogOpen}
          onOpenChange={setMinHourDialogOpen}
          adjustment={editingMinHourRule}
          accountId={activeAccount.id}
          vehicles={vehicles || []}
          existingRules={minHourRules || []}
        />
      )}

      {/* Delete Price Adjustment Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Price Adjustment</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{deletingAdjustment?.name}&quot;? This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deletingAdjustment && deleteMutation.mutate(deletingAdjustment.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Minimum Hour Rule Confirmation Dialog */}
      <AlertDialog open={deleteMinHourDialogOpen} onOpenChange={setDeleteMinHourDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Minimum Hour Rule</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{deletingMinHourRule?.name}&quot;? This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deletingMinHourRule && deleteMinHourMutation.mutate(deletingMinHourRule.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMinHourMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Custom Fee Dialog */}
      {activeAccount && (
        <CustomFeeDialog
          open={customFeeDialogOpen}
          onOpenChange={setCustomFeeDialogOpen}
          fee={editingCustomFee}
          accountId={activeAccount.id}
        />
      )}

      {/* Delete Custom Fee Confirmation Dialog */}
      <AlertDialog open={deleteCustomFeeDialogOpen} onOpenChange={setDeleteCustomFeeDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Custom Fee</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{deletingCustomFee?.name}&quot;? This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deletingCustomFee && deleteCustomFeeMutation.mutate(deletingCustomFee.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteCustomFeeMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
